import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, Alert, Spin, Space, Divider } from 'antd';
import { UserOutlined, LockOutlined, LoginOutlined, DashboardOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;

const Login = () => {
  const [error, setError] = useState('');
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  
  // Mobile responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const onFinish = async (values) => {
    setError('');
    const result = await login(values.email, values.password);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.message);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '20px' : '40px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background Decorations */}
      <div style={{
        position: 'absolute',
        top: '-50%',
        right: '-20%',
        width: '600px',
        height: '600px',
        background: 'rgba(255,255,255,0.1)',
        borderRadius: '50%',
        filter: 'blur(100px)'
      }} />
      <div style={{
        position: 'absolute',
        bottom: '-30%',
        left: '-10%',
        width: '400px',
        height: '400px',
        background: 'rgba(255,255,255,0.05)',
        borderRadius: '50%',
        filter: 'blur(80px)'
      }} />

      <Card
        style={{
          width: isMobile ? '100%' : '420px',
          maxWidth: '420px',
          borderRadius: isMobile ? '24px' : '32px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          border: 'none',
          backdropFilter: 'blur(20px)',
          background: 'rgba(255,255,255,0.95)',
          position: 'relative',
          zIndex: 1
        }}
        bodyStyle={{
          padding: isMobile ? '32px 24px' : '48px 40px'
        }}
      >
        {/* Enhanced Header */}
        <div style={{ textAlign: 'center', marginBottom: isMobile ? '32px' : '40px' }}>
          {/* Logo Container */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '20px',
            width: isMobile ? '80px' : '96px',
            height: isMobile ? '80px' : '96px',
            margin: '0 auto 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 32px rgb(255, 255, 255)',
            padding: '12px'
          }}>
            <img 
              src={require('../styles/logo.png')} 
              alt="Trading Admin Logo"
              style={{ 
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                borderRadius: '8px'
              }} 
            />
          </div>

          <Title level={isMobile ? 3 : 2} style={{ 
            margin: '0 0 8px 0',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontSize: isMobile ? '24px' : '32px',
            fontWeight: 800,
            letterSpacing: '-0.5px'
          }}>
            Trading Admin
          </Title>
          <p style={{ 
            color: '#666', 
            fontSize: isMobile ? '14px' : '16px',
            fontWeight: 500,
            margin: 0
          }}>
            🔐 Sign in to your admin dashboard
          </p>
        </div>

        {error && (
          <Alert
            message="❌ Login Failed"
            description={error}
            type="error"
            showIcon={false}
            style={{ 
              marginBottom: '24px',
              borderRadius: '12px',
              border: '1px solid #ffccc7',
              background: 'linear-gradient(135deg, #fff2f0 0%, #ffebe8 100%)'
            }}
          />
        )}

        <Form
          name="login"
          onFinish={onFinish}
          autoComplete="off"
          size="large"
          layout="vertical"
        >
          <Form.Item
            label={
              <span style={{ 
                fontSize: isMobile ? '14px' : '16px', 
                fontWeight: 600,
                color: '#333'
              }}>
                📧 Email Address
              </span>
            }
            name="email"
            rules={[
              { required: true, message: 'Please input your email!' },
              { type: 'email', message: 'Please enter a valid email!' }
            ]}
          >
            <Input
              prefix={<UserOutlined style={{ color: '#667eea' }} />}
              placeholder="Enter your email address"
              style={{
                height: isMobile ? '48px' : '52px',
                borderRadius: '12px',
                fontSize: isMobile ? '16px' : '14px',
                border: '2px solid #f0f0f0',
                transition: 'all 0.3s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#667eea';
                e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#f0f0f0';
                e.target.style.boxShadow = 'none';
              }}
            />
          </Form.Item>

          <Form.Item
            label={
              <span style={{ 
                fontSize: isMobile ? '14px' : '16px', 
                fontWeight: 600,
                color: '#333'
              }}>
                🔒 Password
              </span>
            }
            name="password"
            rules={[{ required: true, message: 'Please input your password!' }]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#667eea' }} />}
              placeholder="Enter your password"
              style={{
                height: isMobile ? '48px' : '52px',
                borderRadius: '12px',
                fontSize: isMobile ? '16px' : '14px',
                border: '2px solid #f0f0f0',
                transition: 'all 0.3s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#667eea';
                e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#f0f0f0';
                e.target.style.boxShadow = 'none';
              }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: '24px' }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={!loading && <LoginOutlined />}
              style={{
                width: '100%',
                height: isMobile ? '52px' : '56px',
                borderRadius: '12px',
                fontSize: isMobile ? '16px' : '18px',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 12px 32px rgba(102, 126, 234, 0.5)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 8px 24px rgba(102, 126, 234, 0.4)';
                }
              }}
            >
              {loading ? (
                <Space>
                  <Spin size="small" />
                  <span>Signing In...</span>
                </Space>
              ) : (
                '🚀 Sign In to Dashboard'
              )}
            </Button>
          </Form.Item>

          <Divider style={{ 
            margin: '24px 0',
            borderColor: '#f0f0f0'
          }}>
            <span style={{ 
              color: '#999', 
              fontSize: isMobile ? '12px' : '14px',
              fontWeight: 500
            }}>
              New Admin?
            </span>
          </Divider>

          <Form.Item style={{ textAlign: 'center', marginBottom: 0 }}>
            <Button 
              type="link" 
              onClick={() => navigate('/register')}
              style={{
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: 600,
                color: '#667eea',
                padding: isMobile ? '8px 16px' : '12px 24px',
                borderRadius: '8px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(102, 126, 234, 0.1)';
                e.target.style.color = '#764ba2';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = '#667eea';
              }}
            >
              👤 Create Admin Account
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default Login;